import axios from 'axios';

const getBaseUrl = () => {
    return process.env.REACT_APP_API_URL || process.env.VITE_API_URL || 'https://karotrukk-production.up.railway.app';
};

// Create instance
const api = axios.create({
    baseURL: getBaseUrl(),
    withCredentials: true,
    headers: {
        'Content-Type': 'application/json'
    }
});

// Request interceptor for adding Bearer token
api.interceptors.request.use(
    (config) => {
        // If the route is an admin route, use the adminToken
        if (config.url && (config.url.includes('/api/admin') || config.url.includes('/api/v1/admin'))) {
            const adminToken = localStorage.getItem('adminToken');
            if (adminToken) {
                config.headers.Authorization = `Bearer ${adminToken}`;
            }
        } else {
            const token = localStorage.getItem('token');
            if (token) {
                config.headers.Authorization = `Bearer ${token}`;
            }
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

/**
 * Formats raw network/backend errors into concise, clear user messages.
 */
export const getCleanErrorMessage = (error) => {
    // 1. Browser offline / no internet connection
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        return 'No internet connection. Please try again.';
    }

    // 2. Axios network disconnect / timeout
    if (error?.code === 'ERR_NETWORK' || error?.message === 'Network Error' || !error?.response) {
        return 'Network error. Check connection & retry.';
    }

    const status = error.response?.status;
    let serverMsg = error.response?.data?.message || error.response?.data?.error || error.message || '';

    if (typeof serverMsg !== 'string') {
        serverMsg = JSON.stringify(serverMsg);
    }

    // 3. Raw database / DNS / socket connection error strings (e.g. getaddrinfo, ENOTFOUND, MongoNetworkError, ECONNREFUSED)
    if (
        serverMsg.includes('getaddrinfo') ||
        serverMsg.includes('ENOTFOUND') ||
        serverMsg.includes('ECONNREFUSED') ||
        serverMsg.includes('MongoNetworkError') ||
        serverMsg.includes('MongooseError') ||
        serverMsg.includes('ETIMEDOUT') ||
        serverMsg.includes('buffering timed out')
    ) {
        return 'Server offline. Check connection & retry.';
    }

    // 4. Specific HTTP status code mappings for concise reasons
    const requestUrl = error?.config?.url || '';
    const isAuthLoginEndpoint = requestUrl.includes('/admin/auth/login') ||
        requestUrl.includes('/admin/auth/forgot-password') ||
        requestUrl.includes('/admin/auth/reset-password') ||
        requestUrl.includes('/auth/login') ||
        requestUrl.includes('/vehicle-owner/login') ||
        requestUrl.includes('/customer/login') ||
        requestUrl.includes('/driver/login');

    if (isAuthLoginEndpoint) {
        if (serverMsg && !serverMsg.includes('{') && !serverMsg.includes('[object')) {
            return serverMsg;
        }
        return 'Invalid email or password.';
    }

    if (status === 400) {
        if (serverMsg && (serverMsg.toLowerCase().includes('phone or email already') || serverMsg.toLowerCase().includes('account exists already'))) {
            return 'Phone or email already registered.';
        }
        if (serverMsg.toLowerCase().includes('otp') || serverMsg.toLowerCase().includes('code')) {
            return 'Invalid or expired OTP.';
        }
        if (serverMsg.toLowerCase().includes('password')) {
            return serverMsg.length <= 60 ? serverMsg : 'Invalid password provided.';
        }
    }

    if (status === 401) {
        if (error.response?.data?.requiresEmailVerification) {
            return serverMsg;
        }
        if (serverMsg.toLowerCase().includes('invalid corporate') || serverMsg.toLowerCase().includes('corporate email')) {
            return serverMsg;
        }
        if (serverMsg.toLowerCase().includes('invalid credentials') || serverMsg.toLowerCase().includes('wrong password') || serverMsg.toLowerCase().includes('incorrect password')) {
            return serverMsg.length <= 60 ? serverMsg : 'Incorrect corporate email or password.';
        }
        if (serverMsg && !serverMsg.toLowerCase().includes('session expired') && !serverMsg.toLowerCase().includes('jwt') && serverMsg.length <= 60) {
            return serverMsg;
        }
        return 'Session expired. Please log in again.';
    }

    if (status === 403) {
        if (serverMsg && serverMsg.length <= 150) {
            return serverMsg;
        }
        return 'Access denied.';
    }

    if (status === 423) {
        // HTTP 423 Locked (e.g. admin account locked from failed attempts)
        return serverMsg || 'Account temporarily locked. Please try again later.';
    }

    if (status === 404) {
        if (serverMsg && !serverMsg.includes('{')) {
            return serverMsg;
        }
        return 'No account registered with this email ID.';
    }

    if (status === 429) {
        return serverMsg || 'Too many attempts. Wait 1 minute.';
    }

    if (status >= 500) {
        return 'Server error. Please try again shortly.';
    }

    // 5. If server message is clean, return short form
    if (serverMsg && !serverMsg.includes('{') && !serverMsg.includes('[object')) {
        return serverMsg.length > 70 ? serverMsg.substring(0, 67) + '...' : serverMsg;
    }

    return 'An error occurred. Please try again.';
};

// Response interceptor for handling common errors
api.interceptors.response.use(
    (response) => response,
    (error) => {
        // Sanitize error message to prevent technical DNS/network errors from showing to users
        const cleanMessage = getCleanErrorMessage(error);
        if (error.response && error.response.data) {
            error.response.data.message = cleanMessage;
        } else if (error) {
            error.message = cleanMessage;
        }

        if (error.response?.status === 401) {
            // Ignore the global redirect for auth checks so the inline error shows up / background check fails silently
            if (error.config && error.config.url && (
                error.config.url.includes('/api/admin/auth/login') || 
                error.config.url.includes('/api/admin/auth/forgot-password') || 
                error.config.url.includes('/api/admin/auth/reset-password') || 
                error.config.url.includes('/api/admin/auth/verify-reset-token') || 
                error.config.url.includes('/api/admin/auth/me') || 
                error.config.url.includes('/api/admin/auth/logout') || 
                error.config.url.includes('/api/v1/admin/auth/send-otp') ||
                error.config.url.includes('/api/v1/admin/auth/verify-otp') ||
                error.config.url.includes('/api/auth/me') ||
                error.config.url.includes('/api/auth/login') ||
                error.config.url.includes('/api/vehicle-owner/login') ||
                error.config.url.includes('/api/customer/login')
            )) {
                return Promise.reject(error);
            }
            console.error('Unauthorized access - potential token expiration');
            
            // Determine where to redirect based on the URL that failed
            if (error.config && (error.config.url.includes('/api/admin') || error.config.url.includes('/api/v1/admin'))) {
                localStorage.removeItem('adminToken');
                localStorage.removeItem('adminUser');
                window.location.href = '/admin/login';
            } else {
                localStorage.removeItem('token');
                window.location.href = '/login';
            }
        }
        return Promise.reject(error);
    }
);

/**
 * Ensures Request IDs in KaroTrukk start with a single '#' and never generate double hashes '##'.
 */
export const formatRequestId = (id) => {
    if (!id) return '';
    const str = String(id).trim();
    if (!str || str === 'N/A' || str === 'shipment') return '';
    const cleanStr = str.replace(/^#+/, '');
    return `#${cleanStr}`;
};

export default api;
